import type {SuggestionType, Recipient} from 'src/types/contacts';
import type {State} from 'src/reducers';
import type {
  SetOfDiscoverOrTrmCandidateDetails,
  NumbersWithNames,
  MsgExtLocationDetails,
} from 'src/types/draft-messages';
import type {
  BroadcastSuppressionPhoneDetails,
  TimeFrame,
} from 'src/components/messaging/broadcast-suppression/types';
import {createSelector} from 'reselect';
import {CARD_TYPES} from 'src/components/messaging/broadcast-suppression/constants';
import type {AtsContact} from 'src/chat-extension/messages';


export const selectRecipients = (
  state: State,
): Array<SuggestionType | string> => state.draftMessages.recipients;

export const selectRecipientsCount = (state: State): number =>
  state.draftMessages.recipients.length;

export const selectFilesForUpload = (state: State, draftKey: string): File[] =>
  state.draftMessages.drafts[draftKey]?.files || [];

export const selectFileUploadErrors = (
  state: State,
  draftKey: string,
): File[] =>
  // @ts-expect-error - TS2339 - Property 'fileErrors' does not exist on type 'DraftMessagesState'.
  state.draftMessages.drafts[draftKey]?.fileErrors || [];

export const getBroadcastSuppressionTimeFrame = (state: State): TimeFrame => {
  return state.draftMessages.timeFrame;
};

const isBroadcast = (state: State) => {
  const recipients = state.draftMessages.recipients;
  const firstRecipient = recipients[0];
  const isThreadList =
    typeof firstRecipient === 'object' &&
    // @ts-expect-error - TS2339 - Property 'threadListSuggestion' does not exist on type 'SuggestionType'.
    firstRecipient.threadListSuggestion &&
    true;

  return isThreadList || recipients.length > 1;
};
export const getAllRestoredPhoneNumbers = (state: State): string[] => {
  return state.draftMessages.restoredPhoneNumbers;
};

export const getAllRestoredLandlineNumbers = (state: State): string[] => {
  return state.draftMessages.restoredLandlineNumbers;
};

export const getRecentlyMessage = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const phoneTypes: BroadcastSuppressionPhoneDetails[] = Object.values(
    state.draftMessages.phoneTypes,
  );
  const isBroadcastMsg = isBroadcast(state);
  const agent = state.accounts.authedUserId
    ? state.accounts.data[state.accounts.authedUserId]
    : null;
  const broadcastConfig =
    // @ts-expect-error - TS2339 - Property 'broadcastSuppression' does not exist on type 'RoleConfigurations'.
    agent?.securityRole.configurations.broadcastSuppression.type || '';
  const restoredPhoneNumbers = getAllRestoredPhoneNumbers(state);
  const result = phoneTypes.filter(
    (phone) =>
      phone.broadcastSuppression === true &&
      isBroadcastMsg &&
      !restoredPhoneNumbers.includes(phone.phoneNumber),
  );

  return broadcastConfig === 'off' ? [] : result;
};

export const getAllRecentlyMessaged = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const phoneTypes: BroadcastSuppressionPhoneDetails[] = Object.values(
    state.draftMessages.phoneTypes,
  );
  const agent = state.accounts.authedUserId
    ? state.accounts.data[state.accounts.authedUserId]
    : null;
  const broadcastConfig =
    // @ts-expect-error - TS2339 - Property 'broadcastSuppression' does not exist on type 'RoleConfigurations'.
    agent?.securityRole.configurations.broadcastSuppression.type || '';
  const result = phoneTypes.filter(
    (phone) => phone.broadcastSuppression === true,
  );
  return broadcastConfig === 'off' ? [] : result;
};

export const getLandlineNumbers = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const phoneTypes: BroadcastSuppressionPhoneDetails[] = Object.values(
    state.draftMessages.phoneTypes,
  );
  const restoredPhoneNumbers = getAllRestoredPhoneNumbers(state);

  const result = phoneTypes.filter(
    (phone) =>
      !restoredPhoneNumbers.includes(phone.phoneNumber) &&
      phone.type === CARD_TYPES.LANDLINE,
  );
  return result;
};

export const getInvalidNumbers = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const phoneTypes: BroadcastSuppressionPhoneDetails[] = Object.values(
    state.draftMessages.phoneTypes,
  );
  const result = phoneTypes.filter(
    (phone) => phone.type === CARD_TYPES.INVALID,
  );
  return result;
};

export const getOptOutNumbers = (
  state: State,
): BroadcastSuppressionPhoneDetails[] => {
  const phoneTypes: BroadcastSuppressionPhoneDetails[] = Object.values(
    state.draftMessages.phoneTypes,
  );
  const result = phoneTypes.filter((phone) => phone.optedOut);
  return result;
};

export const selectOptedOutPhoneNumberList = (state: State): string[] => {
  return getOptOutNumbers(state).map((p) => p.phoneNumber);
};

export const getBroadcastSuppressionCount: (state: State) => number =
  createSelector(
    selectRecipientsCount,
    getRecentlyMessage,
    getLandlineNumbers,
    getInvalidNumbers,
    getOptOutNumbers,
    (
      recipientsCount,
      recentlyMessaged,
      landlineNumbers,
      invalidNumbers,
      optOutNumbers,
    ) => {
      if (recipientsCount === 0) {
        return 0;
      }
      const recentlyMessagedNumbers = recentlyMessaged.map(
        (phone) => phone.phoneNumber,
      );

      // Count landlines that are NOT recently messaged (to avoid double counting)
      const landlineOnlyCount = landlineNumbers.filter(
        (phone) => !recentlyMessagedNumbers.includes(phone.phoneNumber),
      ).length;
      return (
        recentlyMessaged.length +
        landlineOnlyCount +
        invalidNumbers.length +
        optOutNumbers.length
      );
    },
  );

export const getTypesOfBroadcastWarnings: (state: State) => string[] =
  createSelector(
    getRecentlyMessage,
    getLandlineNumbers,
    getInvalidNumbers,
    getOptOutNumbers,
    (
      recentlyMessaged,
      landlineNumbers,
      invalidNumbers,
      optOutNumbers,
    ): string[] => {
      const allWarnings: Array<string> = [];
      if (recentlyMessaged.length > 0) {
        allWarnings.push(CARD_TYPES.RECENTLY_MESSAGED);
      }
      if (landlineNumbers.length > 0) {
        allWarnings.push(CARD_TYPES.LANDLINE);
      }
      if (invalidNumbers.length > 0) {
        allWarnings.push(CARD_TYPES.INVALID);
      }
      if (optOutNumbers.length > 0) {
        allWarnings.push(CARD_TYPES.OPTOUT);
      }
      return allWarnings;
    },
  );

export const getCurrentlyAppliedSuppression = (state: State): boolean => {
  return state.draftMessages.applySuppression;
};

export const getAllSuppressedPhoneNumbers: (state: State) => string[] =
  createSelector(
    getRecentlyMessage,
    getLandlineNumbers,
    getInvalidNumbers,
    selectOptedOutPhoneNumberList,
    (recentlyMessaged, landlineNumbers, invalidNumbers, optOutPhoneNumbers) => {
      const recentlyMessagedPhones = recentlyMessaged.map(
        (value) => value.phoneNumber,
      );
      const landlinePhoneNumbers = landlineNumbers.map(
        (value) => value.phoneNumber,
      );
      const invalidPhoneNumbers = invalidNumbers.map(
        (value) => value.phoneNumber,
      );
      return [
        ...recentlyMessagedPhones,
        ...landlinePhoneNumbers,
        ...invalidPhoneNumbers,
        ...optOutPhoneNumbers,
      ];
    },
  );

export const selectIsBroadcastSuppressionConfigOff = (
  state: State,
): boolean => {
  const agent = state.accounts.authedUserId
    ? state.accounts.data[state.accounts.authedUserId]
    : null;
  const broadcastConfig =
    // @ts-expect-error - TS2339 - Property 'broadcastSuppression' does not exist on type 'RoleConfigurations'.
    agent?.securityRole.configurations.broadcastSuppression.type || '';

  return broadcastConfig === 'off';
};

export const selectMessageOrigin = (state: State): string => {
  // @ts-expect-error - TS2339 - Property 'origin' does not exist on type 'DraftMessagesState'.
  return state.draftMessages.origin;
};

export const selectDiscoverOrTrmCandidateDetails = (
  state: State,
): SetOfDiscoverOrTrmCandidateDetails => {
  return state.draftMessages.candidateDetails;
};

export const selectIsDiscoverIframeContext = (state: State): boolean => {
  return state.draftMessages.isDiscoverIframeContext;
};

export const selectScrapedHtml = (state: State): string => {
  return state.draftMessages.grabNumbersScrapedHtml;
};

export const selectScrapedNumbersWithNames = (
  state: State,
): NumbersWithNames => {
  // @ts-expect-error - TS2339 - Property 'grabNumbersWithNames' does not exist on type 'DraftMessagesState'.
  return state.draftMessages.grabNumbersWithNames;
};

export const selectExtensionPathDetails = (
  state: State,
): MsgExtLocationDetails => {
  return state.draftMessages.extensionPathDetails;
};

export const selectGrabbedPhoneNumbersDetails = (
  state: State,
): {atsContact: AtsContact}[] => {
  return state.draftMessages.grabbedPhoneNumbers;
};

export const getPhoneTypes = (
  state: State,
): Record<string, BroadcastSuppressionPhoneDetails> => {
  return state.draftMessages.phoneTypes;
};
